import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { createFileRoute, Navigate } from "@tanstack/react-router"
import { useState } from "react"
import { useAuth } from "../hooks/useAuth"
import { extractErrorMessage } from "../lib/api/errors"
import { authClient, signIn } from "../lib/auth-client"
import { IS_PERSONAL_DEVICE } from "../lib/runtime-origin"
import { useProduct, useTenant } from "../lib/tenant"
import { LoginView } from "./-login/LoginView"

/**
 * Post-login destination, restricted to a same-origin path ("/x", never "//host"
 * or an absolute URL) so a crafted login link can't bounce the user elsewhere.
 */
function safeRedirectPath(raw: string | undefined): string {
  if (!raw?.startsWith("/") || raw.startsWith("//") || raw.startsWith("/\\")) return "/"
  return raw
}

/** OAuth authorize query keys the MCP plugin round-trips through `/login`. */
const OAUTH_QUERY_KEYS = [
  "client_id",
  "redirect_uri",
  "response_type",
  "scope",
  "state",
  "code_challenge",
  "code_challenge_method",
  "nonce",
  "prompt",
  "resource",
] as const

function oauthAuthorizeQueryString(): string | null {
  if (typeof window === "undefined") return null
  const params = new URLSearchParams(window.location.search)
  if (!params.get("client_id")) return null
  const forwarded = new URLSearchParams()
  for (const key of OAUTH_QUERY_KEYS) {
    const value = params.get(key)
    if (value) forwarded.set(key, value)
  }
  return forwarded.toString()
}

function LoginPage(): React.JSX.Element | null {
  const [error, setError] = useState("")
  const [loading, setLoading] = useState(false)
  const tenantState = useTenant()
  const branding = tenantState.branding
  const product = useProduct()
  const { user, loading: authLoading } = useAuth()
  const { i18n } = useLingui()
  const { redirect } = Route.useSearch()
  const redirectTarget = safeRedirectPath(redirect)
  const oauthAuthorizeQuery = oauthAuthorizeQueryString()

  if (authLoading) return null
  if (user) {
    // MCP OAuth: already signed in with an authorize request on the query string
    // → send the user back to Better Auth so it can issue the code.
    if (oauthAuthorizeQuery) {
      window.location.replace(`/api/auth/mcp/authorize?${oauthAuthorizeQuery}`)
      return null
    }
    // A non-root target (e.g. a members-only /share/:token link) can sit outside
    // the typed route tree, so navigate via the location rather than <Navigate>.
    if (redirectTarget !== "/") {
      window.location.replace(redirectTarget)
      return null
    }
    return <Navigate to="/" />
  }

  const handleSubmit = async (email: string, password: string, rememberMe: boolean): Promise<void> => {
    setError("")
    setLoading(true)

    // Desktop app and installed mobile/PWA are personal, trusted devices — always
    // keep them signed in (30-day session). A plain browser only persists when the
    // user opts in via "Keep me signed in"; otherwise it's a session cookie that
    // clears on browser close.
    const persist = IS_PERSONAL_DEVICE || rememberMe

    try {
      await signIn.email(
        { email, password, rememberMe: persist },
        {
          async onSuccess(ctx: { data?: { twoFactorRedirect?: boolean } }) {
            if (ctx.data?.twoFactorRedirect) {
              window.location.href = "/two-factor"
              return
            }
            // If the user arrived from an invite link, accept it now that they're
            // signed in, then land on their workspace.
            const inviteId = new URLSearchParams(window.location.search).get("invite")
            if (inviteId) {
              try {
                await authClient.organization.acceptInvitation({ invitationId: inviteId })
              } catch {
                // Non-fatal — they're signed in; the org just won't be auto-joined.
              }
            }
            if (oauthAuthorizeQuery) {
              window.location.href = `/api/auth/mcp/authorize?${oauthAuthorizeQuery}`
              return
            }
            window.location.href = redirectTarget
          },
          onError(ctx: { error?: { message?: string } }) {
            setError(ctx.error?.message || i18n._(msg`Login failed`))
          },
        },
      )
    } catch (err: unknown) {
      setError(extractErrorMessage(err))
    } finally {
      setLoading(false)
    }
  }

  const handleSSO = async (): Promise<void> => {
    if (!branding?.slug) return
    setLoading(true)
    try {
      await (
        authClient as unknown as {
          signIn: { sso: (opts: { organizationSlug: string; callbackURL: string }) => Promise<void> }
        }
      ).signIn.sso({
        organizationSlug: branding.slug,
        callbackURL: oauthAuthorizeQuery ? `/login?${oauthAuthorizeQuery}` : redirectTarget,
      })
    } catch (err: unknown) {
      setError(extractErrorMessage(err))
      setLoading(false)
    }
  }

  const displayName = branding?.name || (product === "voxcanon" ? "Voxcanon" : "Lexicanon")
  // Default Lexicanon (no company-custom login background) gets the brand-art
  // background filling the page with a frosted-glass sign-in card. Voxcanon and
  // any company with a custom loginBackground keep their own full-bleed layout.
  const useArtBackground = product === "lexicanon" && !branding?.loginBackground

  return (
    <LoginView
      product={product}
      branding={branding}
      displayName={displayName}
      error={error}
      loading={loading}
      useArtBackground={useArtBackground}
      showRememberMe={!IS_PERSONAL_DEVICE}
      onSubmit={handleSubmit}
      onSSO={handleSSO}
    />
  )
}

export const Route = createFileRoute("/login")({
  component: LoginPage,
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
})
